import { getSelectedCompanyId } from "../utils/tenantContext";
import { useTenantScope, normalizeScopePlatform } from "../scope/TenantScopeContext";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  apiJson,
  downloadAuthenticatedFile,
} from "../api/client";

const REFRESH_INTERVAL = 10000;

const TABS = [
  { id: "processes", label: "Processes" },
  { id: "services", label: "Services" },
  { id: "files", label: "Files" },
  { id: "registry", label: "Registry & Persistence" },
  { id: "terminal", label: "Terminal" },
  { id: "evidence", label: "Evidence" },
  { id: "operations", label: "Operations" },
];

const ACTIVE_STATUSES = new Set([
  "pending_approval",
  "approved",
  "dispatched",
  "started",
]);

function formatDate(value) {
  if (!value) return "Not recorded";

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return parsed.toLocaleString();
}

const WINDOWS_ONLY_EDR_ACTIONS = new Set([
  "terminal_cmd",
  "terminal_powershell",
  "read_registry_value",
  "set_registry_value",
  "delete_registry_value",
  "collect_event_logs",
  "collect_defender_status",
  "collect_evidence_bundle",
]);

function formatLabel(value) {
  if (!value) return "Unknown";

  return String(value)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function safeObject(value) {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function EDRResponseCenterPage() {
  const globalScope = useTenantScope();
  const [devices, setDevices] = useState([]);
  const [actions, setActions] = useState([]);

  const [selectedDeviceUuid, setSelectedDeviceUuid] = useState("");

  // R12I_EDR_GLOBAL_DEVICE_SYNC
  useEffect(() => {
    if (
      globalScope.deviceUuid &&
      globalScope.deviceUuid !== selectedDeviceUuid
    ) {
      setSelectedDeviceUuid(globalScope.deviceUuid);
    }
  }, [globalScope.deviceUuid, selectedDeviceUuid]);
  const [activeTab, setActiveTab] = useState("processes");

  const [processes, setProcesses] = useState([]);
  const [processSearch, setProcessSearch] = useState("");

  const [services, setServices] = useState([]);
  const [serviceSearch, setServiceSearch] = useState("");
  const [serviceStatusFilter, setServiceStatusFilter] = useState("");

  const [filePath, setFilePath] = useState(
    "C:\\ProgramData\\WirelessPress"
  );
  const [fileItems, setFileItems] = useState([]);
  const [fileSearch, setFileSearch] = useState("");
  const [fileResultPath, setFileResultPath] = useState("");

  const [terminalMode, setTerminalMode] = useState("powershell");
  const [terminalCommand, setTerminalCommand] = useState("");
  const [terminalWorkingDirectory, setTerminalWorkingDirectory] =
    useState("C:\\Windows\\System32");
  const [terminalTimeout, setTerminalTimeout] = useState(60);
  const [terminalTranscript, setTerminalTranscript] = useState([]);

  const [persistenceRecords, setPersistenceRecords] = useState([]);
  const [persistenceSearch, setPersistenceSearch] = useState("");
  const [persistenceTypeFilter, setPersistenceTypeFilter] =
    useState("");

  const [registryPath, setRegistryPath] = useState(
    "HKLM:\\SOFTWARE\\Microsoft\\Windows NT\\CurrentVersion"
  );
  const [registryValueName, setRegistryValueName] =
    useState("ProductName");
  const [registryValueData, setRegistryValueData] = useState("");
  const [registryValueType, setRegistryValueType] =
    useState("String");
  const [registryResult, setRegistryResult] = useState(null);

  const [selectedEventLogs, setSelectedEventLogs] = useState([
    "System",
    "Application",
    "Security",
  ]);
  const [eventMaxEvents, setEventMaxEvents] = useState(100);
  const [eventLogResult, setEventLogResult] = useState(null);
  const [defenderResult, setDefenderResult] = useState(null);
  const [evidenceBundleResult, setEvidenceBundleResult] =
    useState(null);
  const [evidenceDownloadingId, setEvidenceDownloadingId] =
    useState(null);
  const [evidenceArtifacts, setEvidenceArtifacts] = useState([]);
  const [evidenceArtifactsLoading, setEvidenceArtifactsLoading] =
    useState(false);
  const [downloadingArtifactId, setDownloadingArtifactId] =
    useState(null);

  const [loading, setLoading] = useState(true);
  const [submittingAction, setSubmittingAction] = useState("");
  const [actionBusyId, setActionBusyId] = useState(null);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const role = localStorage.getItem("role") || "client_admin";
  const companyId = getSelectedCompanyId() || "";
  const isSuperAdmin = role === "super_admin";

  const selectedDevice = useMemo(
    () =>
      devices.find(
        (device) => device.device_uuid === selectedDeviceUuid
      ) || null,
    [devices, selectedDeviceUuid]
  );


  const selectedPlatform = normalizeScopePlatform(
    selectedDevice?.platform ||
      globalScope.selectedDevice?.platform ||
      ""
  );

  const isWindowsDevice =
    selectedPlatform === "windows";

  const isMacOSDevice =
    selectedPlatform === "macos";


  // R12I_PLATFORM_TAB_CORRECTIVE
  useEffect(() => {
    if (
      !isWindowsDevice &&
      ["registry", "terminal", "evidence"].includes(activeTab)
    ) {
      setActiveTab("processes");
    }
  }, [activeTab, isWindowsDevice]);

  // R12I_PLATFORM_FILE_ROOT
  useEffect(() => {
    if (isMacOSDevice) {
      setFilePath((current) => {
        if (
          !current ||
          current === "C:\\Windows\\System32" ||
          current === "C:\\ProgramData\\WirelessPress"
        ) {
          return "/";
        }

        return current;
      });
    }

    if (isWindowsDevice) {
      setFilePath((current) => {
        if (!current || current === "/") {
          return "C:\\Windows\\System32";
        }

        return current;
      });
    }
  }, [isMacOSDevice, isWindowsDevice]);

  const filteredProcesses = useMemo(() => {
    const query = processSearch.trim().toLowerCase();

    if (!query) return processes;

    return processes.filter((process) =>
      [
        process.name,
        process.username,
        process.executable_path,
        process.command_line,
        process.process_id,
        process.parent_process_id,
      ]
        .filter((value) => value !== null && value !== undefined)
        .some((value) =>
          String(value).toLowerCase().includes(query)
        )
    );
  }, [processSearch, processes]);

  const filteredServices = useMemo(() => {
    const query = serviceSearch.trim().toLowerCase();

    return services.filter((service) => {
      const statusMatches =
        !serviceStatusFilter ||
        String(service.status || "").toLowerCase() ===
          serviceStatusFilter.toLowerCase();

      const queryMatches =
        !query ||
        [
          service.service_name,
          service.display_name,
          service.status,
          service.start_mode,
          service.path,
          service.account,
          service.process_id,
        ]
          .filter(
            (value) =>
              value !== null &&
              value !== undefined
          )
          .some((value) =>
            String(value).toLowerCase().includes(query)
          );

      return statusMatches && queryMatches;
    });
  }, [serviceSearch, serviceStatusFilter, services]);


  const filteredFileItems = useMemo(() => {
    const query = fileSearch.trim().toLowerCase();

    if (!query) return fileItems;

    return fileItems.filter((item) =>
      [
        item.name,
        item.path,
        item.extension,
      ]
        .filter(
          (value) =>
            value !== null &&
            value !== undefined
        )
        .some((value) =>
          String(value).toLowerCase().includes(query)
        )
    );
  }, [fileItems, fileSearch]);

  const filteredPersistenceRecords = useMemo(() => {
    const query = persistenceSearch.trim().toLowerCase();

    return persistenceRecords.filter((record) => {
      const typeMatches =
        !persistenceTypeFilter ||
        record.persistence_type === persistenceTypeFilter;

      const queryMatches =
        !query ||
        [
          record.persistence_type,
          record.location,
          record.name,
          record.command,
          record.user,
          record.status,
        ]
          .filter(
            (value) =>
              value !== null &&
              value !== undefined
          )
          .some((value) =>
            String(value).toLowerCase().includes(query)
          );

      return typeMatches && queryMatches;
    });
  }, [
    persistenceRecords,
    persistenceSearch,
    persistenceTypeFilter,
  ]);

  const loadDevices = useCallback(async () => {
    const path = isSuperAdmin
      ? "/devices"
      : `/devices/company/${companyId}`;

    const data = await apiJson(path);

    const safeDevices = Array.isArray(data)
      ? data
      : Array.isArray(data?.devices)
        ? data.devices
        : [];

    setDevices(safeDevices);

    setSelectedDeviceUuid((current) => {
      if (
        current &&
        safeDevices.some(
          (device) => device.device_uuid === current
        )
      ) {
        return current;
      }

      const preferredDevice =
        safeDevices.find(
          (device) =>
            device.device_uuid &&
            String(device.platform || "")
              .toLowerCase()
              .includes("windows") &&
            String(device.status || "")
              .toLowerCase() === "online"
        ) ||
        safeDevices.find(
          (device) =>
            device.device_uuid &&
            String(device.status || "")
              .toLowerCase() === "online"
        ) ||
        safeDevices.find(
          (device) => device.device_uuid
        );

      return preferredDevice?.device_uuid || "";
    });
  }, [companyId, isSuperAdmin]);

  const loadActions = useCallback(async () => {
    const query = new URLSearchParams();

    query.set("limit", "200");

    if (selectedDeviceUuid) {
      query.set("device_uuid", selectedDeviceUuid);
    }

    const data = await apiJson(
      `/edr/actions?${query.toString()}`
    );

    const safeActions = Array.isArray(data) ? data : [];

    setActions(safeActions);

    const latestProcessCollection = safeActions.find(
      (action) =>
        action.action_type === "collect_processes" &&
        action.status === "completed"
    );

    const result = safeObject(latestProcessCollection?.result);

    if (Array.isArray(result.processes)) {
      setProcesses(result.processes);
    }

    const latestServiceCollection = safeActions.find(
      (action) =>
        action.action_type === "collect_services" &&
        action.status === "completed"
    );

    const serviceResult = safeObject(
      latestServiceCollection?.result
    );

    if (Array.isArray(serviceResult.services)) {
      setServices(serviceResult.services);
    }

    const latestFileBrowse = safeActions.find(
      (action) =>
        action.action_type === "browse_files" &&
        action.status === "completed"
    );

    const fileResult = safeObject(
      latestFileBrowse?.result
    );

    if (Array.isArray(fileResult.items)) {
      setFileItems(fileResult.items);
      setFileResultPath(fileResult.path || "");
    }

    const completedTerminalActions = safeActions
      .filter(
        (action) =>
          ["terminal_cmd", "terminal_powershell"].includes(
            action.action_type
          ) &&
          ["completed", "failed"].includes(action.status)
      )
      .slice()
      .reverse()
      .map((action) => {
        const terminalResult = safeObject(action.result);
        const parameters = safeObject(action.parameters);

        return {
          id: action.id,
          actionUuid: action.action_uuid,
          shell:
            terminalResult.shell ||
            (action.action_type === "terminal_powershell"
              ? "powershell"
              : "cmd"),
          command:
            terminalResult.command ||
            parameters.command ||
            "Unknown command",
          workingDirectory:
            terminalResult.working_directory ||
            parameters.working_directory ||
            "",
          stdout: terminalResult.stdout || "",
          stderr:
            terminalResult.stderr ||
            action.error_message ||
            "",
          exitCode:
            terminalResult.exit_code ??
            (action.status === "completed" ? 0 : null),
          duration:
            terminalResult.duration_seconds ??
            null,
          status: action.status,
          completedAt:
            action.completed_at ||
            action.requested_at,
        };
      });

    setTerminalTranscript(completedTerminalActions);

    const latestPersistenceCollection = safeActions.find(
      (action) =>
        action.action_type === "collect_persistence" &&
        action.status === "completed"
    );

    const persistenceResult = safeObject(
      latestPersistenceCollection?.result
    );

    if (Array.isArray(persistenceResult.persistence)) {
      setPersistenceRecords(
        persistenceResult.persistence
      );
    }

    const latestRegistryRead = safeActions.find(
      (action) =>
        action.action_type === "read_registry_value" &&
        action.status === "completed"
    );

    const latestRegistryResult = safeObject(
      latestRegistryRead?.result
    );

    if (
      latestRegistryResult.registry &&
      typeof latestRegistryResult.registry === "object"
    ) {
      setRegistryResult(
        latestRegistryResult.registry
      );
    }

    const latestEventLogs = safeActions.find(
      (action) =>
        action.action_type === "collect_event_logs" &&
        action.status === "completed"
    );

    const latestEventResult = safeObject(
      latestEventLogs?.result
    );

    if (Object.keys(latestEventResult).length > 0) {
      setEventLogResult(latestEventResult);
    }

    const latestDefenderStatus = safeActions.find(
      (action) =>
        action.action_type === "collect_defender_status" &&
        action.status === "completed"
    );

    const latestDefenderResult = safeObject(
      latestDefenderStatus?.result
    );

    if (Object.keys(latestDefenderResult).length > 0) {
      setDefenderResult(latestDefenderResult);
    }

    const latestEvidenceBundle = safeActions.find(
      (action) =>
        action.action_type === "collect_evidence_bundle" &&
        action.status === "completed"
    );

    const latestBundleResult = safeObject(
      latestEvidenceBundle?.result
    );

    if (Object.keys(latestBundleResult).length > 0) {
      setEvidenceBundleResult(latestBundleResult);
    }
  }, [selectedDeviceUuid]);

  const loadEvidenceArtifacts = useCallback(async () => {
    if (!selectedDeviceUuid) {
      setEvidenceArtifacts([]);
      return;
    }

    try {
      setEvidenceArtifactsLoading(true);

      const query = new URLSearchParams({
        device_uuid: selectedDeviceUuid,
        limit: "100",
      });

      const data = await apiJson(
        `/edr/evidence?${query.toString()}`
      );

      setEvidenceArtifacts(
        Array.isArray(data) ? data : []
      );
    } catch (artifactError) {
      console.error(
        "Evidence artifact load error:",
        artifactError
      );

      setError(
        artifactError.message ||
          "Unable to load evidence artifacts."
      );
    } finally {
      setEvidenceArtifactsLoading(false);
    }
  }, [selectedDeviceUuid]);


  const loadPage = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      await loadDevices();
      await Promise.all([
        loadActions(),
        loadEvidenceArtifacts(),
      ]);
    } catch (loadError) {
      console.error("Live Response load error:", loadError);

      setError(
        loadError.message ||
          "Unable to load the Live Response workspace."
      );
    } finally {
      setLoading(false);
    }
  }, [
    loadActions,
    loadDevices,
    loadEvidenceArtifacts,
  ]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      loadActions().catch((refreshError) => {
        console.error(
          "Live Response refresh error:",
          refreshError
        );
      });
    }, REFRESH_INTERVAL);

    return () => window.clearInterval(timer);
  }, [loadActions]);

  useEffect(() => {
    loadEvidenceArtifacts();
  }, [loadEvidenceArtifacts]);

  const queueAction = async (
    actionType,
    parameters = {},
    options = {}
  ) => {

    // R12I_WINDOWS_ACTION_GATE
    if (
      WINDOWS_ONLY_EDR_ACTIONS.has(actionType) &&
      !isWindowsDevice
    ) {
      const platformLabel =
        selectedDevice?.platform ||
        globalScope.selectedDevice?.platform ||
        selectedPlatform ||
        "unknown";

      setError(
        `${formatLabel(actionType)} is supported only on Windows endpoints. ` +
        `Selected endpoint platform: ${platformLabel}.`
      );

      return null;
    }

    if (!selectedDeviceUuid) {
      setError("Select a managed endpoint.");
      return null;
    }

    if (options.confirmMessage) {
      const confirmed = window.confirm(
        options.confirmMessage
      );

      if (!confirmed) return null;
    }

    try {
      setSubmittingAction(actionType);
      setError("");
      setSuccess("");

      const created = await apiJson("/edr/actions", {
        method: "POST",
        body: JSON.stringify({
          device_uuid: selectedDeviceUuid,
          action_type: actionType,
          priority: options.priority || "normal",
          parameters,
        }),
      });

      setSuccess(
        created.status === "pending_approval"
          ? `${formatLabel(actionType)} queued and awaiting approval.`
          : `${formatLabel(actionType)} queued successfully.`
      );

      await loadActions();

      return created;
    } catch (actionError) {
      console.error(
        `Unable to queue ${actionType}:`,
        actionError
      );

      setError(
        actionError.message ||
          `Unable to queue ${formatLabel(actionType)}.`
      );

      return null;
    } finally {
      setSubmittingAction("");
    }
  };

  const approveAction = async (action) => {
    try {
      setActionBusyId(action.id);
      setError("");
      setSuccess("");

      await apiJson(`/edr/actions/${action.id}/approve`, {
        method: "POST",
        body: JSON.stringify({
          reason: "Approved through Live Response.",
        }),
      });

      setSuccess(
        `${formatLabel(action.action_type)} approved.`
      );

      await loadActions();
    } catch (approveError) {
      setError(
        approveError.message ||
          "Unable to approve the action."
      );
    } finally {
      setActionBusyId(null);
    }
  };

  const cancelAction = async (action) => {
    const confirmed = window.confirm(
      `Cancel ${formatLabel(action.action_type)}?`
    );

    if (!confirmed) return;

    try {
      setActionBusyId(action.id);
      setError("");
      setSuccess("");

      await apiJson(`/edr/actions/${action.id}/cancel`, {
        method: "POST",
        body: JSON.stringify({
          reason: "Cancelled through Live Response.",
        }),
      });

      setSuccess("Action cancelled.");

      await loadActions();
    } catch (cancelError) {
      setError(
        cancelError.message ||
          "Unable to cancel the action."
      );
    } finally {
      setActionBusyId(null);
    }
  };

  const refreshProcesses = async () => {
    await queueAction("collect_processes");
  };

  const killProcess = async (process) => {
    await queueAction(
      "kill_process",
      {
        process_id: Number(process.process_id),
      },
      {
        priority: "high",
        confirmMessage:
          `Terminate ${process.name || "this process"} ` +
          `(PID ${process.process_id})?\n\n` +
          "This destructive action requires approval.",
      }
    );
  };

  const suspendProcess = async (process) => {
    await queueAction(
      "suspend_process",
      {
        process_id: Number(process.process_id),
      },
      {
        priority: "high",
        confirmMessage:
          `Suspend ${process.name || "this process"} ` +
          `(PID ${process.process_id})?`,
      }
    );
  };

  const resumeProcess = async (process) => {
    await queueAction(
      "resume_process",
      {
        process_id: Number(process.process_id),
      },
      {
        confirmMessage:
          `Resume ${process.name || "this process"} ` +
          `(PID ${process.process_id})?`,
      }
    );
  };

  const refreshServices = async () => {
    await queueAction("collect_services");
  };

  const startService = async (service) => {
    await queueAction(
      "start_service",
      {
        service_name: service.service_name,
      },
      {
        confirmMessage:
          `Start service ${service.display_name || service.service_name}?`,
      }
    );
  };

  const stopService = async (service) => {
    await queueAction(
      "stop_service",
      {
        service_name: service.service_name,
      },
      {
        priority: "high",
        confirmMessage:
          `Stop service ${service.display_name || service.service_name}?\n\n` +
          "This action requires approval.",
      }
    );
  };

  const restartService = async (service) => {
    await queueAction(
      "restart_service",
      {
        service_name: service.service_name,
      },
      {
        priority: "high",
        confirmMessage:
          `Restart service ${service.display_name || service.service_name}?`,
      }
    );
  };

  const changeServiceStartMode = async (service, startMode) => {
    if (!startMode) return;

    await queueAction(
      "set_service_start_mode",
      {
        service_name: service.service_name,
        start_mode: startMode,
      },
      {
        priority:
          startMode === "disabled" ? "high" : "normal",
        confirmMessage:
          `Set ${service.display_name || service.service_name} ` +
          `startup mode to ${startMode}?`,
      }
    );
  };


  const browseFiles = async (requestedPath = filePath) => {
    const normalized = String(requestedPath || "").trim();

    if (!normalized) {
      setError("Enter an endpoint directory path.");
      return;
    }

    setFilePath(normalized);

    await queueAction(
      "browse_files",
      {
        file_path: normalized,
        depth: 1,
      }
    );
  };

  const openDirectory = async (item) => {
    if (!item?.is_directory) return;

    await browseFiles(item.path);
  };

  const goToParentDirectory = async () => {
    const current = fileResultPath || filePath;

    if (!current) return;

    const normalized = current.replace(/[\\/]+$/, "");
    const separatorIndex = Math.max(
      normalized.lastIndexOf("\\\\"),
      normalized.lastIndexOf("/")
    );

    if (separatorIndex <= 2) {
      await browseFiles(normalized.slice(0, 3));
      return;
    }

    await browseFiles(
      normalized.slice(0, separatorIndex)
    );
  };

  const hashFile = async (item) => {
    await queueAction(
      "hash_file",
      {
        file_path: item.path,
      }
    );
  };

  const quarantineFile = async (item) => {
    await queueAction(
      "quarantine_file",
      {
        file_path: item.path,
      },
      {
        priority: "high",
        confirmMessage:
          `Quarantine ${item.name}?\\n\\n` +
          `Path: ${item.path}\\n\\n` +
          "The file will be moved into the Wireless Press quarantine vault.",
      }
    );
  };

  const restoreFile = async (item) => {
    await queueAction(
      "restore_quarantined_file",
      {
        file_path: item.path,
      },
      {
        priority: "high",
        confirmMessage:
          `Restore the quarantine record associated with ${item.path}?`,
      }
    );
  };


  const runTerminalCommand = async (event) => {
    event.preventDefault();

    const command = terminalCommand.trim();

    if (!command) {
      setError("Enter a command.");
      return;
    }

    const actionType =
      terminalMode === "powershell"
        ? "terminal_powershell"
        : "terminal_cmd";

    const created = await queueAction(
      actionType,
      {
        command,
        working_directory:
          terminalWorkingDirectory.trim() ||
          "C:\\Windows\\System32",
        timeout_seconds: Number(terminalTimeout),
      },
      {
        priority: "high",
        confirmMessage:
          `Execute this ${terminalMode.toUpperCase()} command on ` +
          `${
            selectedDevice?.hostname ||
            selectedDeviceUuid
          }?\n\n${command}\n\nApproval is required.`,
      }
    );

    if (created) {
      setTerminalCommand("");
    }
  };

  const clearTerminalTranscript = () => {
    setTerminalTranscript([]);
  };

  const copyTerminalTranscript = async () => {
    const transcript = terminalTranscript
      .map((entry) => {
        const prompt =
          entry.shell === "powershell" ? "PS>" : "C:\\>";

        return [
          `[${formatDate(entry.completedAt)}]`,
          `${prompt} ${entry.command}`,
          entry.stdout,
          entry.stderr
            ? `STDERR:\n${entry.stderr}`
            : "",
          `Exit code: ${
            entry.exitCode === null
              ? "Unknown"
              : entry.exitCode
          }`,
        ]
          .filter(Boolean)
          .join("\n");
      })
      .join("\n\n");

    await navigator.clipboard.writeText(transcript);

    setSuccess("Terminal transcript copied.");
  };


  const refreshPersistence = async () => {
    await queueAction("collect_persistence");
  };

  const readRegistryValue = async (event) => {
    event.preventDefault();

    const pathValue = registryPath.trim();

    if (!pathValue) {
      setError("Enter a registry path.");
      return;
    }

    await queueAction(
      "read_registry_value",
      {
        registry_path: pathValue,
        value_name: registryValueName.trim(),
      }
    );
  };

  const setRegistryValue = async () => {
    const pathValue = registryPath.trim();
    const nameValue = registryValueName.trim();

    if (!pathValue || !nameValue) {
      setError(
        "Registry path and value name are required."
      );
      return;
    }

    let parsedValue = registryValueData;

    if (
      ["DWord", "QWord"].includes(registryValueType)
    ) {
      const numericValue = Number(registryValueData);

      if (!Number.isFinite(numericValue)) {
        setError(
          `${registryValueType} data must be numeric.`
        );
        return;
      }

      parsedValue = numericValue;
    }

    await queueAction(
      "set_registry_value",
      {
        registry_path: pathValue,
        value_name: nameValue,
        value_type: registryValueType,
        value_data: parsedValue,
      },
      {
        priority: "high",
        confirmMessage:
          `Set registry value?\n\n` +
          `${pathValue}\\${nameValue}\n\n` +
          `Type: ${registryValueType}\n` +
          `Data: ${registryValueData}\n\n` +
          "Approval is required.",
      }
    );
  };

  const deleteRegistryValue = async () => {
    const pathValue = registryPath.trim();
    const nameValue = registryValueName.trim();

    if (!pathValue || !nameValue) {
      setError(
        "Registry path and value name are required."
      );
      return;
    }

    await queueAction(
      "delete_registry_value",
      {
        registry_path: pathValue,
        value_name: nameValue,
      },
      {
        priority: "critical",
        confirmMessage:
          `Delete registry value?\n\n` +
          `${pathValue}\\${nameValue}\n\n` +
          "This cannot be automatically restored. Approval is required.",
      }
    );
  };


  const toggleEventLog = (logName) => {
    setSelectedEventLogs((current) =>
      current.includes(logName)
        ? current.filter((item) => item !== logName)
        : [...current, logName]
    );
  };

  const collectEventLogs = async () => {
    if (selectedEventLogs.length === 0) {
      setError("Select at least one event log.");
      return;
    }

    await queueAction(
      "collect_event_logs",
      {
        log_names: selectedEventLogs,
        max_events: Number(eventMaxEvents),
      }
    );
  };

  const collectDefenderStatus = async () => {
    await queueAction("collect_defender_status");
  };

  const collectEvidenceBundle = async () => {
    await queueAction(
      "collect_evidence_bundle",
      {
        include_event_logs: true,
        include_defender: true,
        include_processes: true,
        include_services: true,
        include_network: true,
        log_names: selectedEventLogs,
        max_events: Number(eventMaxEvents),
      },
      {
        priority: "high",
        confirmMessage:
          "Create a complete endpoint evidence bundle?\n\n" +
          "This collects logs, processes, services, network data, " +
          "and Microsoft Defender status. Approval is required.",
      }
    );
  };



  const downloadEvidenceArtifact = async (artifact) => {
    try {
      setDownloadingArtifactId(artifact.id);
      setError("");
      setSuccess("");

      await downloadAuthenticatedFile(
        `/edr/evidence/${artifact.id}/download`,
        artifact.original_filename ||
          `${artifact.bundle_id}.zip`
      );

      setSuccess(
        `Evidence bundle ${artifact.bundle_id} downloaded.`
      );
    } catch (downloadError) {
      setError(
        downloadError.message ||
          "Unable to download the evidence bundle."
      );
    } finally {
      setDownloadingArtifactId(null);
    }
  };

  const canCancel = (status) =>
    ![
      "dispatched",
      "started",
      "completed",
      "failed",
      "cancelled",
      "expired",
    ].includes(status);

  const activeActions = actions.filter((action) =>
    ACTIVE_STATUSES.has(action.status)
  ).length;

  const completedActions = actions.filter(
    (action) => action.status === "completed"
  ).length;

  const failedActions = actions.filter(
    (action) => action.status === "failed"
  ).length;

  if (loading) {
    return (
      <div className="edr-response-page">
        <div className="edr-response-loading">
          Loading Live Response…
        </div>
      </div>
    );
  }

  return (
    <div className="edr-response-page live-response-page">
      <header className="edr-response-header">
        <div>
          <span className="edr-response-eyebrow">
            Endpoint Detection and Response
          </span>

          <h1>Live Response Workspace</h1>

          <p>
            Investigate and respond to managed endpoints through
            authenticated, approved, and fully audited actions.
          </p>
        </div>

        <button
          type="button"
          className="edr-secondary-button"
          onClick={loadPage}
        >
          Refresh Workspace
        </button>
      </header>

      {error && (
        <div className="edr-message edr-message-error">
          {error}
        </div>
      )}

      {success && (
        <div className="edr-message edr-message-success">
          {success}
        </div>
      )}

      <section className="live-response-device-bar">
        <label>
          Endpoint

          <select
            value={selectedDeviceUuid}
            onChange={(event) => {
              setSelectedDeviceUuid(event.target.value);
              setProcesses([]);
              setServices([]);
              setFileItems([]);
              setFileResultPath("");
              setPersistenceRecords([]);
              setRegistryResult(null);
              setEventLogResult(null);
              setDefenderResult(null);
              setEvidenceBundleResult(null);
            }}
          >
            <option value="">Select an endpoint</option>

            {devices
              .filter((device) => device.device_uuid)
              .filter(
                (device) =>
                  !globalScope.platform ||
                  globalScope.platform === "all" ||
                  normalizeScopePlatform(device.platform) ===
                    globalScope.platform
              )
              .map((device) => (
                <option
                  key={device.id || device.device_uuid}
                  value={device.device_uuid}
                >
                  {device.hostname ||
                    device.device_name ||
                    device.device_uuid}
                  {device.company_name
                    ? ` — ${device.company_name}`
                    : ""}
                </option>
              ))}
          </select>
        </label>

        {selectedDevice && (
          <div className="live-response-device-summary">
            <strong>
              {selectedDevice.hostname ||
                selectedDevice.device_name ||
                "Managed endpoint"}
            </strong>

            <span>{selectedDevice.device_uuid}</span>

            <span>
              {selectedDevice.platform || "Unknown platform"}
              {" · "}
              {selectedDevice.status || "Status unavailable"}
            </span>
          </div>
        )}
      </section>

      <section className="edr-metric-grid">
        <article>
          <span>Managed Devices</span>
          <strong>{devices.length}</strong>
        </article>

        <article>
          <span>Active Actions</span>
          <strong>{activeActions}</strong>
        </article>

        <article>
          <span>Completed</span>
          <strong>{completedActions}</strong>
        </article>

        <article>
          <span>Failed</span>
          <strong>{failedActions}</strong>
        </article>
      </section>

      <nav className="live-response-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={
              activeTab === tab.id
                ? "live-response-tab active"
                : "live-response-tab"
            }
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {activeTab === "processes" && (
        <section className="edr-panel live-response-workspace">
          <div className="edr-panel-heading">
            <div>
              <h2>Process Explorer</h2>

              <p>
                Review running processes and queue approved response
                actions.
              </p>
            </div>

            <div className="live-response-toolbar">
              <input
                type="search"
                placeholder="Search process, user, path, PID…"
                value={processSearch}
                onChange={(event) =>
                  setProcessSearch(event.target.value)
                }
              />

              <button
                type="button"
                className="edr-primary-button"
                onClick={refreshProcesses}
                disabled={
                  !selectedDeviceUuid ||
                  submittingAction === "collect_processes"
                }
              >
                {submittingAction === "collect_processes"
                  ? "Collecting…"
                  : "Refresh Processes"}
              </button>
            </div>
          </div>

          <div className="edr-action-table-wrap">
            <table className="edr-action-table live-response-table">
              <thead>
                <tr>
                  <th>PID</th>
                  <th>Process</th>
                  <th>User</th>
                  <th>Parent PID</th>
                  <th>Memory</th>
                  <th>Command Line</th>
                  <th>Controls</th>
                </tr>
              </thead>

              <tbody>
                {filteredProcesses.length > 0 ? (
                  filteredProcesses.map((process) => (
                    <tr
                      key={`${process.process_id}-${process.name}`}
                    >
                      <td>{process.process_id}</td>

                      <td>
                        <strong>{process.name || "Unknown"}</strong>
                        <small>
                          {process.executable_path ||
                            "Path unavailable"}
                        </small>
                      </td>

                      <td>{process.username || "SYSTEM/Unknown"}</td>

                      <td>
                        {process.parent_process_id ?? "Unknown"}
                      </td>

                      <td>
                        {process.working_set_bytes
                          ? `${(
                              process.working_set_bytes /
                              1024 /
                              1024
                            ).toFixed(1)} MB`
                          : "Unavailable"}
                      </td>

                      <td className="live-response-command-cell">
                        {process.command_line || "Not available"}
                      </td>

                      <td>
                        <div className="edr-row-actions">
                          <button
                            type="button"
                            onClick={() =>
                              suspendProcess(process)
                            }
                          >
                            Suspend
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              resumeProcess(process)
                            }
                          >
                            Resume
                          </button>

                          <button
                            type="button"
                            className="edr-danger-button"
                            onClick={() =>
                              killProcess(process)
                            }
                          >
                            Kill
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7">
                      <div className="edr-empty-state">
                        Select an endpoint and click Refresh Processes.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {activeTab === "services" && (
        <section className="edr-panel live-response-workspace">
          <div className="edr-panel-heading">
            <div>
              <h2>Services Manager</h2>

              <p>
                Review endpoint services and queue controlled service
                operations supported by the selected platform.
              </p>
            </div>

            <div className="live-response-toolbar">
              <input
                type="search"
                placeholder="Search service, account, path…"
                value={serviceSearch}
                onChange={(event) =>
                  setServiceSearch(event.target.value)
                }
              />

              <select
                value={serviceStatusFilter}
                onChange={(event) =>
                  setServiceStatusFilter(event.target.value)
                }
              >
                <option value="">All statuses</option>
                <option value="Running">Running</option>
                <option value="Stopped">Stopped</option>
              </select>

              <button
                type="button"
                className="edr-primary-button"
                onClick={refreshServices}
                disabled={
                  !selectedDeviceUuid ||
                  submittingAction === "collect_services"
                }
              >
                {submittingAction === "collect_services"
                  ? "Collecting…"
                  : "Refresh Services"}
              </button>
            </div>
          </div>

          <div className="edr-action-table-wrap">
            <table className="edr-action-table live-response-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Status</th>
                  <th>Startup</th>
                  <th>Account</th>
                  <th>PID</th>
                  <th>Path</th>
                  <th>Controls</th>
                </tr>
              </thead>

              <tbody>
                {filteredServices.length > 0 ? (
                  filteredServices.map((service) => (
                    <tr key={service.service_name}>
                      <td>
                        <strong>
                          {service.display_name ||
                            service.service_name}
                        </strong>

                        <small>{service.service_name}</small>
                      </td>

                      <td>
                        <span
                          className={`edr-status ${
                            String(service.status).toLowerCase() ===
                            "running"
                              ? "edr-status-completed"
                              : ""
                          }`}
                        >
                          {service.status || "Unknown"}
                        </span>
                      </td>

                      <td>{service.start_mode || "Unknown"}</td>

                      <td>{service.account || "Unknown"}</td>

                      <td>{service.process_id || "—"}</td>

                      <td className="live-response-command-cell">
                        {service.path || "Unavailable"}
                      </td>

                      <td>
                        <div className="edr-row-actions live-service-actions">
                          {String(service.status).toLowerCase() ===
                          "running" ? (
                            <>
                              <button
                                type="button"
                                onClick={() =>
                                  restartService(service)
                                }
                              >
                                Restart
                              </button>

                              <button
                                type="button"
                                className="edr-danger-button"
                                onClick={() =>
                                  stopService(service)
                                }
                              >
                                Stop
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() =>
                                startService(service)
                              }
                            >
                              Start
                            </button>
                          )}

                          <select
                            defaultValue=""
                            aria-label={`Change startup mode for ${
                              service.display_name ||
                              service.service_name
                            }`}
                            onChange={(event) => {
                              changeServiceStartMode(
                                service,
                                event.target.value
                              );

                              event.target.value = "";
                            }}
                          >
                            <option value="">
                              Startup mode
                            </option>
                            <option value="automatic">
                              Automatic
                            </option>
                            <option value="manual">
                              Manual
                            </option>
                            <option value="disabled">
                              Disabled
                            </option>
                          </select>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7">
                      <div className="edr-empty-state">
                        Select an endpoint and click Refresh Services.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {activeTab === "files" && (
        <section className="edr-panel live-response-workspace">
          <div className="edr-panel-heading">
            <div>
              <h2>Remote File Browser</h2>

              <p>
                Browse endpoint directories, calculate hashes, and
                quarantine suspicious files.
              </p>
            </div>
          </div>

          <div className="live-file-toolbar">
            <button
              type="button"
              className="edr-secondary-button"
              onClick={goToParentDirectory}
              disabled={!selectedDeviceUuid}
            >
              Parent Folder
            </button>

            <input
              type="text"
              value={filePath}
              onChange={(event) =>
                setFilePath(event.target.value)
              }
              placeholder="C:\ProgramData\WirelessPress"
            />

            <button
              type="button"
              className="edr-primary-button"
              onClick={() => browseFiles()}
              disabled={
                !selectedDeviceUuid ||
                submittingAction === "browse_files"
              }
            >
              {submittingAction === "browse_files"
                ? "Browsing…"
                : "Browse"}
            </button>

            <input
              type="search"
              value={fileSearch}
              onChange={(event) =>
                setFileSearch(event.target.value)
              }
              placeholder="Filter current directory…"
            />
          </div>

          <div className="live-file-current-path">
            <strong>Current directory:</strong>
            <span>{fileResultPath || filePath}</span>
          </div>

          <div className="edr-action-table-wrap">
            <table className="edr-action-table live-response-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Size</th>
                  <th>Modified</th>
                  <th>Path</th>
                  <th>Controls</th>
                </tr>
              </thead>

              <tbody>
                {filteredFileItems.length > 0 ? (
                  filteredFileItems.map((item) => (
                    <tr key={item.path}>
                      <td>
                        {item.is_directory ? (
                          <button
                            type="button"
                            className="live-file-link"
                            onClick={() =>
                              openDirectory(item)
                            }
                          >
                            📁 {item.name}
                          </button>
                        ) : (
                          <strong>📄 {item.name}</strong>
                        )}

                        {item.error && (
                          <small className="edr-result-error">
                            {item.error}
                          </small>
                        )}
                      </td>

                      <td>
                        {item.is_directory
                          ? "Directory"
                          : item.extension || "File"}
                      </td>

                      <td>
                        {item.is_directory ||
                        item.size === null ||
                        item.size === undefined
                          ? "—"
                          : `${(
                              Number(item.size) / 1024
                            ).toFixed(1)} KB`}
                      </td>

                      <td>
                        {formatDate(item.modified_at)}
                      </td>

                      <td className="live-response-command-cell">
                        {item.path}
                      </td>

                      <td>
                        {!item.is_directory && !item.error && (
                          <div className="edr-row-actions">
                            <button
                              type="button"
                              onClick={() => hashFile(item)}
                            >
                              SHA-256
                            </button>

                            <button
                              type="button"
                              className="edr-danger-button"
                              onClick={() =>
                                quarantineFile(item)
                              }
                            >
                              Quarantine
                            </button>

                            {String(item.path)
                              .toLowerCase()
                              .includes("\\quarantine\\") && (
                              <button
                                type="button"
                                onClick={() =>
                                  restoreFile(item)
                                }
                              >
                                Restore
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6">
                      <div className="edr-empty-state">
                        Enter a directory path and click Browse.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {activeTab === "registry" && isWindowsDevice && (
        <section className="registry-live-response-layout">
          <article className="edr-panel live-response-workspace">
            <div className="edr-panel-heading">
              <div>
                <h2>Persistence Inventory</h2>

                <p>
                  Review startup commands, Run keys, scheduled tasks,
                  and automatic services.
                </p>
              </div>

              <button
                type="button"
                className="edr-primary-button"
                onClick={refreshPersistence}
                disabled={
                  !selectedDeviceUuid ||
                  submittingAction ===
                    "collect_persistence"
                }
              >
                {submittingAction ===
                "collect_persistence"
                  ? "Collecting…"
                  : "Collect Persistence"}
              </button>
            </div>

            <div className="live-response-toolbar">
              <input
                type="search"
                value={persistenceSearch}
                onChange={(event) =>
                  setPersistenceSearch(
                    event.target.value
                  )
                }
                placeholder="Search persistence records…"
              />

              <select
                value={persistenceTypeFilter}
                onChange={(event) =>
                  setPersistenceTypeFilter(
                    event.target.value
                  )
                }
              >
                <option value="">All types</option>
                <option value="registry_run_key">
                  Registry Run Keys
                </option>
                <option value="startup_command">
                  Startup Commands
                </option>
                <option value="scheduled_task">
                  Scheduled Tasks
                </option>
                <option value="automatic_service">
                  Automatic Services
                </option>
              </select>
            </div>

            <div className="edr-action-table-wrap">
              <table className="edr-action-table live-response-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Name</th>
                    <th>Location</th>
                    <th>User</th>
                    <th>Status</th>
                    <th>Command</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredPersistenceRecords.length > 0 ? (
                    filteredPersistenceRecords.map(
                      (record, index) => (
                        <tr
                          key={`${record.persistence_type}-${record.location}-${record.name}-${index}`}
                        >
                          <td>
                            {formatLabel(
                              record.persistence_type
                            )}
                          </td>

                          <td>
                            <strong>
                              {record.name || "Unnamed"}
                            </strong>
                          </td>

                          <td className="live-response-command-cell">
                            {record.location || "Unknown"}
                          </td>

                          <td>{record.user || "Unknown"}</td>

                          <td>
                            {record.status || "Present"}
                          </td>

                          <td className="live-response-command-cell">
                            {record.command || "Unavailable"}
                          </td>
                        </tr>
                      )
                    )
                  ) : (
                    <tr>
                      <td colSpan="6">
                        <div className="edr-empty-state">
                          Select an endpoint and click Collect
                          Persistence.
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </article>

          <article className="edr-panel registry-editor-panel">
            <div className="edr-panel-heading">
              <div>
                <h2>Registry Value Inspector</h2>

                <p>
                  Read or queue approved changes to HKLM and HKCU
                  registry values.
                </p>
              </div>
            </div>

            <form
              className="registry-editor-form"
              onSubmit={readRegistryValue}
            >
              <label>
                Registry path

                <input
                  type="text"
                  value={registryPath}
                  onChange={(event) =>
                    setRegistryPath(event.target.value)
                  }
                  placeholder="HKLM:\SOFTWARE\..."
                />
              </label>

              <label>
                Value name

                <input
                  type="text"
                  value={registryValueName}
                  onChange={(event) =>
                    setRegistryValueName(
                      event.target.value
                    )
                  }
                  placeholder="ProductName"
                />
              </label>

              <label>
                Value type

                <select
                  value={registryValueType}
                  onChange={(event) =>
                    setRegistryValueType(
                      event.target.value
                    )
                  }
                >
                  <option value="String">String</option>
                  <option value="ExpandString">
                    Expand String
                  </option>
                  <option value="DWord">DWORD</option>
                  <option value="QWord">QWORD</option>
                  <option value="MultiString">
                    Multi String
                  </option>
                  <option value="Binary">Binary</option>
                </select>
              </label>

              <label>
                Value data

                <textarea
                  rows="5"
                  value={registryValueData}
                  onChange={(event) =>
                    setRegistryValueData(
                      event.target.value
                    )
                  }
                  placeholder="Used only when setting a value"
                />
              </label>

              <div className="registry-editor-actions">
                <button
                  type="submit"
                  className="edr-primary-button"
                  disabled={
                    !selectedDeviceUuid ||
                    submittingAction ===
                      "read_registry_value"
                  }
                >
                  Read Value
                </button>

                <button
                  type="button"
                  onClick={setRegistryValue}
                  disabled={!selectedDeviceUuid}
                >
                  Set Value
                </button>

                <button
                  type="button"
                  className="edr-danger-button"
                  onClick={deleteRegistryValue}
                  disabled={!selectedDeviceUuid}
                >
                  Delete Value
                </button>
              </div>
            </form>

            <div className="registry-result-panel">
              <h3>Latest Read Result</h3>

              {registryResult ? (
                <pre>
                  {JSON.stringify(
                    registryResult,
                    null,
                    2
                  )}
                </pre>
              ) : (
                <p>No registry value has been read yet.</p>
              )}
            </div>
          </article>
        </section>
      )}

      {activeTab === "terminal" && isWindowsDevice && (
        <section className="edr-panel live-terminal-panel">
          <div className="edr-panel-heading">
            <div>
              <h2>Live Terminal</h2>

              <p>
                Execute approved and fully audited CMD or PowerShell
                commands on the selected endpoint.
              </p>
            </div>

            <div className="live-terminal-header-actions">
              <button
                type="button"
                className="edr-secondary-button"
                onClick={copyTerminalTranscript}
                disabled={terminalTranscript.length === 0}
              >
                Copy Transcript
              </button>

              <button
                type="button"
                className="edr-secondary-button"
                onClick={clearTerminalTranscript}
                disabled={terminalTranscript.length === 0}
              >
                Clear Screen
              </button>
            </div>
          </div>

          <form
            className="live-terminal-command-form"
            onSubmit={runTerminalCommand}
          >
            <label>
              Shell

              <select
                value={terminalMode}
                onChange={(event) =>
                  setTerminalMode(event.target.value)
                }
              >
                <option value="powershell">PowerShell</option>
                <option value="cmd">Command Prompt</option>
              </select>
            </label>

            <label className="live-terminal-directory">
              Working directory

              <input
                type="text"
                value={terminalWorkingDirectory}
                onChange={(event) =>
                  setTerminalWorkingDirectory(
                    event.target.value
                  )
                }
              />
            </label>

            <label>
              Timeout

              <select
                value={terminalTimeout}
                onChange={(event) =>
                  setTerminalTimeout(
                    Number(event.target.value)
                  )
                }
              >
                <option value={30}>30 seconds</option>
                <option value={60}>60 seconds</option>
                <option value={120}>2 minutes</option>
                <option value={300}>5 minutes</option>
              </select>
            </label>

            <div className="live-terminal-command-row">
              <span className="live-terminal-prompt">
                {terminalMode === "powershell"
                  ? "PS>"
                  : "C:\\>"}
              </span>

              <input
                type="text"
                value={terminalCommand}
                onChange={(event) =>
                  setTerminalCommand(event.target.value)
                }
                placeholder={
                  terminalMode === "powershell"
                    ? "Get-Process | Select-Object -First 10"
                    : "hostname"
                }
                autoComplete="off"
              />

              <button
                type="submit"
                className="edr-primary-button"
                disabled={
                  !selectedDeviceUuid ||
                  !terminalCommand.trim() ||
                  submittingAction ===
                    "terminal_powershell" ||
                  submittingAction === "terminal_cmd"
                }
              >
                {submittingAction.startsWith("terminal_")
                  ? "Queueing…"
                  : "Run"}
              </button>
            </div>
          </form>

          <div className="live-terminal-console">
            {terminalTranscript.length > 0 ? (
              terminalTranscript.map((entry) => (
                <article
                  key={entry.id}
                  className="live-terminal-entry"
                >
                  <div className="live-terminal-meta">
                    <span>
                      {formatDate(entry.completedAt)}
                    </span>

                    <span>
                      {entry.shell.toUpperCase()}
                    </span>

                    <span>
                      Exit:{" "}
                      {entry.exitCode === null
                        ? "Unknown"
                        : entry.exitCode}
                    </span>

                    {entry.duration !== null && (
                      <span>{entry.duration}s</span>
                    )}
                  </div>

                  <div className="live-terminal-input">
                    <span>
                      {entry.shell === "powershell"
                        ? "PS>"
                        : "C:\\>"}
                    </span>

                    <code>{entry.command}</code>
                  </div>

                  {entry.stdout && (
                    <pre className="live-terminal-stdout">
                      {entry.stdout}
                    </pre>
                  )}

                  {entry.stderr && (
                    <pre className="live-terminal-stderr">
                      {entry.stderr}
                    </pre>
                  )}
                </article>
              ))
            ) : (
              <div className="live-terminal-empty">
                No terminal commands have completed for this endpoint.
              </div>
            )}
          </div>

          <div className="live-terminal-notice">
            Every command is recorded in the EDR audit trail and
            requires approval before execution.
          </div>
        </section>
      )}

      {activeTab === "evidence" && isWindowsDevice && (
        <section className="evidence-response-layout">
          <article className="edr-panel">
            <div className="edr-panel-heading">
              <div>
                <h2>Evidence Collection</h2>

                <p>
                  Collect endpoint logs, Defender information, and
                  investigation bundles through the audited EDR channel.
                </p>
              </div>
            </div>

            <div className="evidence-control-grid">
              <section className="evidence-control-card">
                <h3>Windows Event Logs</h3>

                <div className="evidence-log-options">
                  {[
                    "System",
                    "Application",
                    "Security",
                    "Windows PowerShell",
                    "Microsoft-Windows-PowerShell/Operational",
                    "Microsoft-Windows-Windows Defender/Operational",
                  ].map((logName) => (
                    <label key={logName}>
                      <input
                        type="checkbox"
                        checked={selectedEventLogs.includes(
                          logName
                        )}
                        onChange={() =>
                          toggleEventLog(logName)
                        }
                      />

                      <span>{logName}</span>
                    </label>
                  ))}
                </div>

                <label>
                  Maximum events per log

                  <select
                    value={eventMaxEvents}
                    onChange={(event) =>
                      setEventMaxEvents(
                        Number(event.target.value)
                      )
                    }
                  >
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={500}>500</option>
                    <option value={1000}>1,000</option>
                  </select>
                </label>

                <button
                  type="button"
                  className="edr-primary-button"
                  onClick={collectEventLogs}
                  disabled={
                    !selectedDeviceUuid ||
                    submittingAction ===
                      "collect_event_logs"
                  }
                >
                  {submittingAction ===
                  "collect_event_logs"
                    ? "Collecting…"
                    : "Collect Event Logs"}
                </button>
              </section>

              <section className="evidence-control-card">
                <h3>Microsoft Defender</h3>

                <p>
                  Collect protection status, signature versions,
                  scan history, and recent threat detections.
                </p>

                <button
                  type="button"
                  className="edr-primary-button"
                  onClick={collectDefenderStatus}
                  disabled={
                    !selectedDeviceUuid ||
                    submittingAction ===
                      "collect_defender_status"
                  }
                >
                  {submittingAction ===
                  "collect_defender_status"
                    ? "Collecting…"
                    : "Collect Defender Status"}
                </button>
              </section>

              <section className="evidence-control-card">
                <h3>Full Evidence Bundle</h3>

                <p>
                  Create a ZIP archive containing processes, services,
                  network connections, event logs, Defender status,
                  a manifest, and SHA-256 hashes.
                </p>

                <button
                  type="button"
                  className="edr-danger-button"
                  onClick={collectEvidenceBundle}
                  disabled={
                    !selectedDeviceUuid ||
                    submittingAction ===
                      "collect_evidence_bundle"
                  }
                >
                  {submittingAction ===
                  "collect_evidence_bundle"
                    ? "Creating Bundle…"
                    : "Create Evidence Bundle"}
                </button>
              </section>
            </div>
          </article>

          <article className="edr-panel evidence-results-panel">
            <div className="edr-panel-heading">
              <div>
                <h2>Latest Evidence Results</h2>

                <p>
                  Results returned by the selected endpoint.
                </p>
              </div>
            </div>

            <div className="evidence-result-grid">
              <section>
                <h3>Event Logs</h3>

                {eventLogResult ? (
                  <>
                    <dl className="evidence-summary-list">
                      <div>
                        <dt>Total events</dt>
                        <dd>
                          {eventLogResult.total_events ?? 0}
                        </dd>
                      </div>

                      <div>
                        <dt>Logs requested</dt>
                        <dd>
                          {safeArray(
                            eventLogResult.requested_logs
                          ).length}
                        </dd>
                      </div>

                      <div>
                        <dt>Collected</dt>
                        <dd>
                          {formatDate(
                            eventLogResult.collected_at
                          )}
                        </dd>
                      </div>
                    </dl>

                    <details>
                      <summary>View event-log result</summary>

                      <pre>
                        {JSON.stringify(
                          eventLogResult,
                          null,
                          2
                        )}
                      </pre>
                    </details>
                  </>
                ) : (
                  <p>No event logs have been collected.</p>
                )}
              </section>

              <section>
                <h3>Defender Status</h3>

                {defenderResult ? (
                  <pre>
                    {JSON.stringify(
                      defenderResult,
                      null,
                      2
                    )}
                  </pre>
                ) : (
                  <p>
                    No Microsoft Defender status has been collected.
                  </p>
                )}
              </section>

              <section className="evidence-artifacts-section">
                <div className="evidence-artifacts-heading">
                  <div>
                    <h3>Uploaded Evidence Bundles</h3>

                    <p>
                      Server-side evidence archives available for
                      authenticated download.
                    </p>
                  </div>

                  <button
                    type="button"
                    className="edr-secondary-button"
                    onClick={loadEvidenceArtifacts}
                    disabled={
                      !selectedDeviceUuid ||
                      evidenceArtifactsLoading
                    }
                  >
                    {evidenceArtifactsLoading
                      ? "Refreshing…"
                      : "Refresh Bundles"}
                  </button>
                </div>

                {evidenceArtifacts.length > 0 ? (
                  <div className="evidence-artifact-list">
                    {evidenceArtifacts.map((artifact) => (
                      <article
                        key={artifact.id}
                        className="evidence-artifact-card"
                      >
                        <div>
                          <strong>
                            {artifact.original_filename ||
                              `${artifact.bundle_id}.zip`}
                          </strong>

                          <span>
                            Bundle: {artifact.bundle_id}
                          </span>

                          <span>
                            Uploaded:{" "}
                            {formatDate(artifact.uploaded_at)}
                          </span>

                          <span>
                            Size:{" "}
                            {artifact.file_size
                              ? `${(
                                  artifact.file_size /
                                  1024 /
                                  1024
                                ).toFixed(2)} MB`
                              : "Unknown"}
                          </span>

                          <code>{artifact.sha256}</code>
                        </div>

                        <button
                          type="button"
                          className="edr-primary-button"
                          onClick={() =>
                            downloadEvidenceArtifact(artifact)
                          }
                          disabled={
                            downloadingArtifactId ===
                            artifact.id
                          }
                        >
                          {downloadingArtifactId === artifact.id
                            ? "Downloading…"
                            : "Download ZIP"}
                        </button>
                      </article>
                    ))}
                  </div>
                ) : (
                  <p>
                    No uploaded evidence bundles are available for this
                    endpoint.
                  </p>
                )}

                {evidenceBundleResult && (
                  <details>
                    <summary>
                      View latest endpoint bundle result
                    </summary>

                    <pre>
                      {JSON.stringify(
                        evidenceBundleResult,
                        null,
                        2
                      )}
                    </pre>
                  </details>
                )}
              </section>
            </div>
          </article>

          <article className="edr-panel evidence-artifacts-panel">
            <div className="edr-panel-heading">
              <div>
                <h2>Uploaded Evidence Archives</h2>
                <p>
                  Evidence securely uploaded from the selected endpoint.
                </p>
              </div>

              <button
                type="button"
                className="edr-secondary-button"
                onClick={loadEvidenceArtifacts}
              >
                Refresh Archives
              </button>
            </div>

            <div className="edr-action-table-wrap">
              <table className="edr-action-table">
                <thead>
                  <tr>
                    <th>Bundle</th>
                    <th>Endpoint</th>
                    <th>Size</th>
                    <th>SHA-256</th>
                    <th>Uploaded</th>
                    <th>Download</th>
                  </tr>
                </thead>

                <tbody>
                  {evidenceArtifacts.length > 0 ? (
                    evidenceArtifacts.map((artifact) => (
                      <tr key={artifact.id}>
                        <td>
                          <strong>{artifact.bundle_id}</strong>
                          <small>
                            {artifact.original_filename ||
                              `${artifact.bundle_id}.zip`}
                          </small>
                        </td>

                        <td>
                          {artifact.hostname ||
                            artifact.device_uuid}
                        </td>

                        <td>
                          {artifact.file_size
                            ? `${(
                                Number(artifact.file_size) /
                                1024 /
                                1024
                              ).toFixed(2)} MB`
                            : "Unknown"}
                        </td>

                        <td className="evidence-hash">
                          {artifact.sha256}
                        </td>

                        <td>
                          {formatDate(artifact.uploaded_at)}
                        </td>

                        <td>
                          <button
                            type="button"
                            className="edr-primary-button"
                            onClick={() =>
                              downloadEvidenceArtifact(artifact)
                            }
                            disabled={
                              evidenceDownloadingId === artifact.id
                            }
                          >
                            {evidenceDownloadingId === artifact.id
                              ? "Downloading…"
                              : "Download ZIP"}
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6">
                        <div className="edr-empty-state">
                          No uploaded evidence archives are available
                          for this endpoint.
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </article>
        </section>
      )}

      {activeTab === "operations" && (
        <section className="edr-panel edr-operations-panel">
          <div className="edr-panel-heading">
            <div>
              <h2>Response Operations</h2>
              <p>
                Approval, dispatch, execution, and result history.
              </p>
            </div>
          </div>

          <div className="edr-action-table-wrap">
            <table className="edr-action-table">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Requested</th>
                  <th>Result</th>
                  <th>Controls</th>
                </tr>
              </thead>

              <tbody>
                {actions.length > 0 ? (
                  actions.map((action) => {
                    const result = safeObject(action.result);
                    const isBusy = actionBusyId === action.id;

                    return (
                      <tr key={action.id}>
                        <td>
                          <strong>
                            {formatLabel(action.action_type)}
                          </strong>

                          <small>{action.action_uuid}</small>
                        </td>

                        <td>
                          <span
                            className={`edr-status edr-status-${action.status}`}
                          >
                            {formatLabel(action.status)}
                          </span>
                        </td>

                        <td>
                          {formatLabel(action.priority)}
                        </td>

                        <td>
                          {formatDate(action.requested_at)}
                        </td>

                        <td className="edr-result-cell">
                          {action.error_message ? (
                            <span className="edr-result-error">
                              {action.error_message}
                            </span>
                          ) : Object.keys(result).length > 0 ? (
                            <details>
                              <summary>View result</summary>

                              <pre>
                                {JSON.stringify(result, null, 2)}
                              </pre>
                            </details>
                          ) : (
                            <span>Pending</span>
                          )}
                        </td>

                        <td>
                          <div className="edr-row-actions">
                            {action.status ===
                              "pending_approval" && (
                              <button
                                type="button"
                                onClick={() =>
                                  approveAction(action)
                                }
                                disabled={isBusy}
                              >
                                Approve
                              </button>
                            )}

                            {canCancel(action.status) && (
                              <button
                                type="button"
                                className="edr-danger-button"
                                onClick={() =>
                                  cancelAction(action)
                                }
                                disabled={isBusy}
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6">
                      <div className="edr-empty-state">
                        No response actions are available.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

export default EDRResponseCenterPage;
